// import i18n from 'i18n';
import { FC, memo, useCallback } from 'react';
import s from './Account.module.scss';
import { useToggle } from 'lib/hooks/useToggle';
import { Button } from 'components/common/Button';
import {
  useChangeUserEmailMutation,
  useDeleteAccountMutation,
  useGetUserDataQuery,
} from 'store/features/user/user-api';
import { PortalComponent } from 'components/common/PortalComponent';
import { Loader } from 'components/common/Loader/Loader';
import { ErrorModal } from 'components/common/ErrorModal/ErrorModal';
import { useQueryError } from 'lib/hooks/useQueryError';
import { useTranslation } from 'react-i18next';
import { Modal } from 'components/common/Modal/Modal';
import { useNavigate, useOutletContext } from 'react-router';
import { ContactField } from './ContactField/ContactField';
import { formatPhoneNumber } from 'lib/utils/common';
import { ReactComponent as Lock } from 'images/lock.svg';
import { ReactComponent as Remove } from 'images/remove.svg';
import { userLogout } from 'store/features/user/utils';
import { useAppDispatch } from 'store/hooks';
import { ModalNavigationContext } from 'components/pages/ConsultantPage/ConsultantPage';

const icons = {
  lock: <Lock />,
  remove: <Remove />,
};

type AccountProps = {};

export const Account: FC<AccountProps> = memo(() => {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { closeRouteModal, goBack } =
    useOutletContext<ModalNavigationContext>();

  const { data } = useGetUserDataQuery();
  const {
    lastName = '',
    firstName = '',
    phone = '',
    email = '',
    birthDate = '',
  } = data || {};

  const formattedBirthDate = birthDate
    ? new Date(`${birthDate}T00:00:00`).toLocaleDateString(
        i18n.resolvedLanguage || i18n.language,
        { day: 'numeric', month: 'long', year: 'numeric' },
      )
    : t('common.notSet');

  // const [
  //   changeName,
  //   {
  //     isLoading: isChangeNameLoading,
  //     isError: isChangeNameError,
  //     error: changeNameError,
  //   },
  // ] = useChangUserNameMutation();

  const [
    changeEmail,
    {
      isLoading: isChangeEmailLoading,
      isError: isChangeEmailError,
      error: changeEmailError,
      isSuccess: isChangeEmailSuccess,
      data: changeEmailData,
      reset: resetChangeEmail,
    },
  ] = useChangeUserEmailMutation();
  const [
    deleteAccount,
    {
      isLoading: isDeleteAccountLoading,
      isError: isDeleteAccountError,
      error: deleteAccountError,
    },
  ] = useDeleteAccountMutation();

  const { showError, errorMessage, closeError } = useQueryError(
    isDeleteAccountError || isChangeEmailError,
    deleteAccountError || changeEmailError,
  );

  const [isOpenRemoveAccount, setIsOpenRemoveAccount] = useToggle(false);

  const handleSubmitEmail = useCallback(
    async (newEmail: string) => {
      try {
        await changeEmail(newEmail).unwrap();
      } catch (err) {
        console.error('Change email error:', err);
      }
    },
    [changeEmail],
  );
  // Заглушка: эндпоинт смены телефона появится позже
  const handleSubmitPhone = useCallback((_value: string) => {}, []);

  const handleChangePassword = useCallback(() => {
    navigate(`change-password`);
  }, [navigate]);

  const handleToggleRemoveAccount = useCallback(() => {
    setIsOpenRemoveAccount((prev) => !prev);
  }, [setIsOpenRemoveAccount]);

  const handleDeleteAccount = useCallback(async () => {
    try {
      await deleteAccount().unwrap();
      userLogout(dispatch, navigate);
    } catch (err) {
      console.error('Delete account error:', err);
    }
  }, [deleteAccount, dispatch, navigate]);

  return (
    <Modal
      title={t('profile.account.title')}
      onClose={closeRouteModal}
      onBack={goBack}
    >
      <div className={s['container']}>
        <ContactField
          type='email'
          title={t('auth.fields.email')}
          value={email}
          isLoading={isChangeEmailLoading}
          onSubmit={handleSubmitEmail}
        />
        <ContactField
          type='tel'
          title={t('auth.fields.phone')}
          value={phone}
          placeholder={t('auth.fields.phonePlaceholder')}
          formatValue={formatPhoneNumber}
          onSubmit={handleSubmitPhone}
        />

        <dl className={s['details']}>
          <div className={s['field']}>
            <dt className={s['field__title']}>{t('auth.fields.firstName')}</dt>
            <dd className={s['field__value']}>{firstName}</dd>
          </div>
          <div className={s['field']}>
            <dt className={s['field__title']}>{t('auth.fields.lastName')}</dt>
            <dd className={s['field__value']}>{lastName}</dd>
          </div>
          <div className={s['birth-date']}>
            <dt>{t('auth.fields.birthDate')}</dt>
            <dd className={s['birth-date__value']}>
              {birthDate ? (
                <time dateTime={birthDate}>{formattedBirthDate}</time>
              ) : (
                formattedBirthDate
              )}
            </dd>
          </div>
        </dl>

        <Button className={s['password-btn']} onClick={handleChangePassword}>
          {icons.lock}
          <span>{t('profile.account.changePassword')}</span>
        </Button>

        <Button className={s['remove-btn']} onClick={handleToggleRemoveAccount}>
          {icons.remove}
          <span>{t('profile.account.deleteAccount')}</span>
        </Button>
      </div>

      {isOpenRemoveAccount && (
        <PortalComponent>
          <Modal
            className={s['remove-account-modal']}
            title={t('profile.account.deleteAccountDescription')}
          >
            <div className={s['remove-account']}>
              <Button
                className={s['remove-account__cancel-btn']}
                onClick={handleToggleRemoveAccount}
                isRounded={true}
                isAccent={true}
              >
                {t('common.cancel')}
              </Button>
              <Button
                className={s['remove-account__remove-btn']}
                onClick={handleDeleteAccount}
              >
                {t('common.remove')}
              </Button>
            </div>
          </Modal>
        </PortalComponent>
      )}

      {isChangeEmailSuccess && (
        <PortalComponent>
          <Modal
            className={s['change-email-modal']}
            title={t('profile.account.changeEmailTitle')}
          >
            <div className={s['change-email']}>
              <p>{changeEmailData?.message}</p>

              <Button
                className={s['change-email__btn']}
                onClick={resetChangeEmail}
                isRounded={true}
                isAccent={true}
              >
                {t('common.return')}
              </Button>
            </div>
          </Modal>
        </PortalComponent>
      )}

      {isDeleteAccountLoading && <Loader />}

      {showError && <ErrorModal message={errorMessage} onClose={closeError} />}
    </Modal>
  );
});
Account.displayName = 'Account';
